// Activity Service — logs every significant user action and reads the admin
// Activity Log.
// log() writes to Supabase activity_events through the shared logger.
// getSessions / getPeopleRoster / getDocumentEngagement read admin-only RPCs.

import { supabase } from '../lib/supabase';
import { logEvent } from '../lib/activityLogger';

export const ACTION_TYPES = {
    // Auth
    USER_LOGIN: 'user_login',
    USER_LOGOUT: 'user_logout',
    // Documents
    DOCUMENT_VIEWED: 'document_viewed',
    DOCUMENT_UPLOADED: 'document_uploaded',
    DOCUMENT_DELETED: 'document_deleted',
    DOCUMENT_DOWNLOADED: 'document_downloaded',
    FOLDER_CREATED: 'folder_created',
    // Users
    USER_CREATED: 'user_created',
    USER_EDITED: 'user_edited',
    USER_DELETED: 'user_deleted',
    PROFILE_UPDATED: 'profile_updated',
    PROFILE_EDIT_REQUESTED: 'profile_edit_requested',
    PROFILE_EDIT_APPROVED: 'profile_edit_approved',
    PROFILE_EDIT_DENIED: 'profile_edit_denied',
    // ACC
    ACC_SUBMITTED: 'acc_submitted',
    ACC_VIEWED: 'acc_viewed',
    ACC_STATUS_CHANGED: 'acc_status_changed',
    ACC_NOTE_ADDED: 'acc_note_added',
    // Inquiries
    INQUIRY_SUBMITTED: 'inquiry_submitted',
    INQUIRY_VIEWED: 'inquiry_viewed',
    INQUIRY_REPLIED: 'inquiry_replied',
    INQUIRY_STATUS_CHANGED: 'inquiry_status_changed',
    // Events
    EVENT_CREATED: 'event_created',
    EVENT_VIEWED: 'event_viewed',
    EVENT_EDITED: 'event_edited',
    EVENT_DELETED: 'event_deleted',
    // Settings
    SETTINGS_CHANGED: 'settings_changed',
    TAB_VISIBILITY_CHANGED: 'tab_visibility_changed',
};

// ─── Activity Log (admin analytics) ──────────────────────────────────────────
// Sessions, the people roster and document engagement are computed server-side
// by admin-only SECURITY DEFINER RPCs (migration 20260930211233). The browser
// only pages through their results.

// Diagnostic events that are never counted as activity. Historical rows are
// shown collapsed as "+N background events".
export const BACKGROUND_EVENT_TYPES = [
    'documents_fetch_started',
    'documents_fetch_success',
    'documents_tab_opened',
    'documents_fetch_error',
];

// Session-feed filter pills, in display order. `types: null` means no filter.
export const SESSION_CATEGORIES = [
    { key: 'all', label: 'All', types: null },
    { key: 'signins', label: 'Sign-ins', types: ['login', 'logout'] },
    { key: 'browsing', label: 'Browsing', types: ['section_view'] },
    {
        key: 'library',
        label: 'Library',
        types: [
            'document_view', 'document_viewed', 'document_search', 'document_download',
            'document_downloaded', 'document_downloaded_mobile', 'document_upload',
            'document_uploaded', 'document_deleted',
        ],
    },
    { key: 'ai', label: 'AI Search', types: ['rag_query', 'rag_result_clicked', 'rag_feedback'] },
    { key: 'inbox', label: 'Inbox', types: ['message_sent', 'message_replied'] },
    {
        key: 'acc',
        label: 'ACC',
        types: [
            'acc_request_submitted', 'acc_request_viewed', 'acc_decision_rendered', 'acc_submitted',
            'acc_viewed', 'acc_status_changed', 'acc_note_added', 'acc_application_submitted',
            'acc_application_comment_added', 'acc_application_decided', 'acc_application_archived',
            'acc_application_deleted',
        ],
    },
    { key: 'events', label: 'Events', types: ['event_created', 'event_viewed', 'event_edited', 'event_deleted'] },
    {
        key: 'account',
        label: 'Account admin',
        types: [
            'user_created', 'user_edited', 'user_deleted', 'user_role_changed', 'profile_updated',
            'profile_edit_requested', 'profile_edit_approved', 'profile_edit_denied',
        ],
    },
];

const SESSION_PAGE_MAX = 100; // RPC caps p_limit at 101 (page + 1 look-ahead row)

// True for events written while an admin was viewing as another user.
export const isViewAsEvent = (event) => {
    const meta = event?.metadata || {};
    return meta._impersonated === true
        || meta._impersonated === 'true'
        || ['admin_view_as', 'admin_returned'].includes(meta.note)
        || ['impersonation_started', 'impersonation_ended'].includes(event?.event_type);
};

/**
 * One page of server-side sessions, newest first.
 * @returns {Promise<{sessions: Array, nextCursor: string|null}>}
 */
export const getSessions = async ({
    hoaId, before = null, limit = 25, category = 'all', userId = null,
    includeAdmin = false, includeArchived = false, search = '',
} = {}) => {
    if (!hoaId) return { sessions: [], nextCursor: null };
    const pageSize = Math.min(Math.max(Number(limit) || 25, 1), SESSION_PAGE_MAX);
    const types = SESSION_CATEGORIES.find((c) => c.key === category)?.types || null;
    const trimmed = (search || '').trim();

    const { data, error } = await supabase.rpc('admin_activity_sessions', {
        p_hoa_id: hoaId,
        p_before: before || null,
        p_limit: pageSize + 1,
        p_event_types: types,
        p_user_id: userId || null,
        p_include_admin: Boolean(includeAdmin),
        p_include_archived: Boolean(includeArchived),
        p_search: trimmed || null,
    });
    if (error) throw error;

    const rows = data || [];
    const sessions = rows.slice(0, pageSize);
    const nextCursor = rows.length > pageSize ? sessions[sessions.length - 1]?.ended_at || null : null;
    return { sessions, nextCursor };
};

/**
 * Raw events of one session, oldest first. Admin-only via activity_events RLS.
 * `viewAs` selects either the view-as events or the genuine ones, matching how
 * the server split the session.
 */
export const getSessionEvents = async ({ hoaId, userId, from, to, viewAs = false } = {}) => {
    if (!hoaId || !userId || !from || !to) return [];
    const { data, error } = await supabase
        .from('activity_events')
        .select('id, event_type, metadata, occurred_at, user_id')
        .eq('hoa_id', hoaId)
        .eq('user_id', userId)
        .gte('occurred_at', from)
        .lte('occurred_at', to)
        .order('occurred_at', { ascending: true })
        .limit(500);
    if (error) throw error;
    return (data || [])
        .filter((event) => isViewAsEvent(event) === Boolean(viewAs))
        .map((event) => ({
            ...event,
            isBackground: BACKGROUND_EVENT_TYPES.includes(event.event_type),
        }));
};

/** People roster: one row per person plus pending invitees (person_key 'i:<email>'). */
export const getPeopleRoster = async ({ hoaId, includeArchived = false, includeExcluded = false } = {}) => {
    if (!hoaId) return [];
    const { data, error } = await supabase.rpc('admin_activity_roster', {
        p_hoa_id: hoaId,
        p_include_archived: Boolean(includeArchived),
        p_include_excluded: Boolean(includeExcluded),
    });
    if (error) throw error;
    return data || [];
};

/** Most-opened documents over the last `days` days. */
export const getDocumentEngagement = async ({ hoaId, days = 90, includeExcluded = false, limit = 10 } = {}) => {
    if (!hoaId) return [];
    const { data, error } = await supabase.rpc('admin_document_engagement', {
        p_hoa_id: hoaId,
        p_days: days,
        p_include_excluded: Boolean(includeExcluded),
        p_limit: limit,
    });
    if (error) throw error;
    return data || [];
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Onboarding funnel from roster rows. Pure. Skips excluded (admin/test),
 * archived and admin rows, and reports how many of each were hidden.
 */
export const summarizeFunnel = (rows = [], now = new Date()) => {
    const nowMs = new Date(now).getTime();
    const hidden = { admins: 0, test: 0, archived: 0 };
    const counted = [];

    rows.forEach((row) => {
        const isAdmin = row.role_name === 'admin';
        if (row.account_state === 'archived') {
            hidden.archived += 1;
            return;
        }
        if (isAdmin) {
            hidden.admins += 1;
            return;
        }
        if (row.is_excluded) {
            hidden.test += 1;
            return;
        }
        counted.push(row);
    });

    const withinDays = (value, days) => Boolean(value) && nowMs - new Date(value).getTime() <= days * DAY_MS;
    const inviteToVisitHours = counted
        .filter((row) => row.invited_at && row.first_login_at)
        .map((row) => (new Date(row.first_login_at).getTime() - new Date(row.invited_at).getTime()) / 3600000)
        .sort((a, b) => a - b);

    let medianHours = null;
    if (inviteToVisitHours.length) {
        const mid = Math.floor(inviteToVisitHours.length / 2);
        medianHours = inviteToVisitHours.length % 2
            ? inviteToVisitHours[mid]
            : (inviteToVisitHours[mid - 1] + inviteToVisitHours[mid]) / 2;
    }

    return {
        invited: counted.filter((row) => row.invited_at).length,
        visited: counted.filter((row) => row.first_login_at).length,
        active7: counted.filter((row) => withinDays(row.last_seen_at, 7)).length,
        active30: counted.filter((row) => withinDays(row.last_seen_at, 30)).length,
        neverVisited: counted.filter((row) => row.invited_at && !row.first_login_at).length,
        medianInviteToVisitHours: medianHours,
        hidden,
    };
};

export const activityService = {
    log: async (actionType, actor = {}, details = {}) =>
        logEvent(actionType, {
            actor_name: actor.name || null,
            actor_roles: actor.roles || [actor.portalRole || actor.role].filter(Boolean),
            ...details,
        }),

    getSessions,
    getSessionEvents,
    getPeopleRoster,
    getDocumentEngagement,
    summarizeFunnel,

    formatAction: (event) => {
        const d = event.details;
        const name = event.actor_name || d?.actor_name || 'Unknown';

        // Impersonation shim: historic rows use event_type='login' with metadata.note
        // set. Relabel those so they don't read as a genuine user login.
        if (event.action === 'login' && d?.note === 'admin_view_as') {
            return `Admin started viewing as ${name}`;
        }
        if (event.action === 'login' && d?.note === 'admin_returned') {
            return `Admin returned to their own account`;
        }

        const templates = {
            login:                  `${name} logged in${d?.role ? ` as ${d.role}` : ''}`,
            logout:                 `${name} logged out`,
            user_login:             `${name} logged in`,
            user_logout:            `${name} logged out`,
            section_view:           `${name} opened ${d?.section || 'a portal section'}`,
            document_view:          `${name} opened "${d?.title || d?.document_id}"`,
            document_viewed:        `${name} viewed "${d?.document_name || d?.title}"`,
            document_upload:        `${name} uploaded "${d?.title || d?.document_id}"`,
            document_uploaded:      `${name} uploaded "${d?.document_name || d?.title}"`,
            document_deleted:       `${name} deleted "${d?.document_name || d?.title}"`,
            document_download:      `${name} downloaded "${d?.title || d?.document_id}"`,
            document_downloaded:    `${name} downloaded "${d?.document_name || d?.title}"`,
            document_downloaded_mobile: `${name} downloaded "${d?.title || d?.document_id}"`,
            documents_tab_opened:   `${name} opened the document library`,
            documents_fetch_started: `${name} refreshed document data`,
            documents_fetch_success: `${name} loaded document data`,
            documents_fetch_error:  `${name} could not load document data`,
            rag_query:              `${name} searched: "${(d?.query || '').substring(0, 80)}"`,
            rag_feedback:           `${name} rated an AI search answer`,
            message_sent:           `${name} sent a new ${d?.type === 'acc' ? 'ACC application' : 'message'}: "${d?.subject || ''}"`,
            message_replied:        `${name} replied to a message thread`,
            acc_submitted:          `${name} submitted ACC application for ${d?.address}`,
            acc_status_changed:     `${name} changed ACC status to "${d?.status || d?.new_status}"`,
            acc_note_added:         `${name} added note to ACC #${d?.acc_id}`,
            acc_application_submitted: `${name} submitted ACC application "${d?.project_title || d?.application_id || ''}"`,
            acc_application_comment_added: `${name} commented on an ACC application`,
            acc_application_decided: `${name} decided an ACC application`,
            acc_application_archived: `${name} archived an ACC application`,
            acc_application_deleted: `${name} deleted an ACC application`,
            profile_updated:        `${name} updated their profile`,
            profile_edit_requested: `${name} requested profile update`,
            profile_edit_approved:  `Profile update for ${d?.user_name} approved`,
            profile_edit_denied:    `Profile update for ${d?.user_name} denied`,
            user_created:           `${name} created user "${d?.user_name}"`,
            user_edited:            `${name} edited "${d?.user_name}"`,
            user_deleted:           `${name} deleted "${d?.user_name}"`,
            event_created:          `${name} created event "${d?.event_title || ''}"`,
            event_edited:           `${name} edited event "${d?.event_title || ''}"`,
            event_deleted:          `${name} deleted event "${d?.event_title || ''}"`,
            settings_changed:       `${name} changed settings: ${d?.setting_name}`,
            tab_visibility_changed: `${name} updated tab visibility settings`,
            inquiry_submitted:      `${name} submitted inquiry: "${d?.subject}"`,
            inquiry_replied:        `${name} replied to inquiry from ${d?.member_name}`,
            impersonation_started:  `${name} started viewing as ${d?.impersonated_as_name || 'a user'}`,
            impersonation_ended:    `${name} stopped viewing as a user`,
        };

        return templates[event.action] || `${name} performed ${event.action}`;
    },
};
